import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, DatePicker, Input, Modal, Select, Table, Tag, Tooltip, message } from 'antd';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { makePostCall, getUserDetails, hisStatusOptions } from '../../utils/helper';
import { StudyDateTime } from '../../components/StudyDateTime';
import FyzksInput from '../../components/FyzksInput';
import QuickDatePresets from '../../components/QuickDatePresets';
import EditStudyDetailsModal from '../../components/EditStudyDetailsModal';
import { useSites } from '../../hooks/useSites';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';
import { FieldLabels, getPacsStatusLabel } from '../../constants/fieldLabels';

const { RangePicker } = DatePicker;

const statusOptions = [
  { label: getPacsStatusLabel('SCANNED'), value: 'SCANNED' },
  { label: 'DRAFTED', value: 'DRAFTED' },
  { label: 'REVIEWED', value: 'REVIEWED' },
  { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
  { label: 'NOT FOR REPORTING', value: 'NOT_FOR_REPORTING' },
];

const statusColors: Record<string, string> = {
  SCANNED: 'blue',
  DRAFTED: 'orange',
  REVIEWED: 'purple',
  SIGNEDOFF: 'green',
};

/**
 * Settings > Manage Studies (admin only).
 *
 * Lists studies with the same /pacs-list filters as the PACS page and offers two
 * admin actions per study:
 *  - DICOM Edit: same EditStudyDetailsModal (/update-study-details) as PACS.
 *  - Delete: PERMANENT delete via /admin/delete-study — clean-deletes the study in
 *    dcm4chee (so the same instances can be re-uploaded as a fresh study) and clears
 *    all app-side reporting/study artefacts, reverting the order to PENDING.
 */
const ManageStudies = () => {
  const isAdmin = getUserDetails()?.user_type === 'admin';

  const [filters, setFilters] = useState<Record<string, any>>({});
  const [dateRange, setDateRange] = useState<any>([
    dayjs().subtract(1, 'day').startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const [studies, setStudies] = useState({ data: [], loading: false, total: 0 });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const [editModal, setEditModal] = useState<{ visible: boolean; record: any }>({ visible: false, record: null });
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; record: any }>({ visible: false, record: null });
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { centers } = useSites();
  const { modalityOptions } = useAllowedModalities();

  // Kept in refs so the debounced fetch always uses the latest values without
  // being recreated (which would defeat the debounce).
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  const fetchStudies = useCallback((page = 1, pageSize = 20) => {
    setStudies(prev => ({ ...prev, loading: true }));
    const payload: Record<string, any> = {
      ...filtersRef.current,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize,
    };
    const dr = dateRangeRef.current;
    if (dr) {
      payload['from_date'] = dayjs(dr[0]).format('YYYYMMDD');
      payload['to_date'] = dayjs(dr[1]).format('YYYYMMDD');
      payload['po_study_date'] = dr;
    }
    makePostCall('/pacs-list', payload)
      .then(res => {
        setStudies({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0,
        });
      })
      .catch(e => {
        console.error('Failed to fetch studies', e);
        setStudies({ data: [], loading: false, total: 0 });
      });
  }, []);

  const debouncedFetch = useMemo(
    () => debounce(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
      fetchStudies(1, 20);
    }, 500),
    [fetchStudies]
  );

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [filters, dateRange, debouncedFetch]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const submitDelete = () => {
    const orderId = deleteModal.record?.pacs_order?.pacs_ord_id;
    if (!orderId) return;
    setDeleting(true);
    makePostCall('/admin/delete-study', {
      order_id: orderId,
      reason: deleteReason.trim() || undefined,
    })
      .then(res => {
        message.success(res.data?.message || 'Study permanently deleted');
        setDeleteModal({ visible: false, record: null });
        setDeleteReason('');
        fetchStudies(pagination.current, pagination.pageSize);
      })
      .catch(e => {
        message.error(e?.response?.data?.message || e.message || 'Failed to delete study');
      })
      .finally(() => setDeleting(false));
  };

  const columns = [
    {
      title: FieldLabels.PATIENT_NAME,
      key: 'pat_name',
      width: 180,
      ellipsis: true,
      render: (_, rec) => rec?.pacs_order?.patient?.pat_name || '-',
    },
    {
      title: FieldLabels.PATIENT_ID,
      key: 'pat_pin',
      width: 110,
      render: (_, rec) => rec?.pacs_order?.patient?.pat_pin || '-',
    },
    {
      title: FieldLabels.ACCESSION_NUMBER_SHORTEST,
      key: 'po_acc_no',
      width: 120,
      render: (_, rec) => rec?.pacs_order?.po_acc_no || '-',
    },
    {
      title: 'Modality',
      key: 'po_modality',
      width: 80,
      render: (_, rec) => rec?.pacs_order?.po_modality || '-',
    },
    {
      title: FieldLabels.STUDY_DESCRIPTION,
      key: 'po_diag_desc',
      ellipsis: true,
      render: (_, rec) => rec?.pacs_order?.po_diag_desc || '-',
    },
    {
      title: FieldLabels.STUDY_DATE,
      key: 'ps_study_dt_tm',
      width: 150,
      render: (_, rec) => <StudyDateTime value={rec?.ps_study_dt_tm} offset={false} />,
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, rec) => {
        const status = rec?.order_workflow?.ow_pacs_status;
        return status ? <Tag color={statusColors[status] || 'default'}>{status}</Tag> : '-';
      },
    },
    {
      title: 'Site',
      key: 'po_site',
      width: 130,
      ellipsis: true,
      render: (_, rec) => rec?.pacs_order?.po_site || '-',
    },
    {
      title: FieldLabels.ACTIONS,
      key: 'actions',
      width: 110,
      fixed: 'right' as const,
      render: (_, rec) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Tooltip title="DICOM Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => setEditModal({ visible: true, record: rec })}
            />
          </Tooltip>
          <Tooltip title="Delete study (permanent)">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setDeleteReason('');
                setDeleteModal({ visible: true, record: rec });
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  if (!isAdmin) {
    return <div style={{ padding: 24 }}>Manage Studies is available to admin users only.</div>;
  }

  const deleteRec = deleteModal.record;

  return (
    <div style={{ padding: 12 }}>
      <h3 style={{ marginBottom: 12 }}>Manage Studies</h3>

      {/* Filter bar — same fields / filter keys as the PACS list */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 160 }}>
          <FyzksInput
            size="small"
            placeholder={FieldLabels.PATIENT_NAME}
            value={filters['pat_name'] || ''}
            onChange={e => handleFilterChange('pat_name', e.target.value)}
          />
        </div>
        <div style={{ width: 130 }}>
          <FyzksInput
            size="small"
            placeholder={FieldLabels.PATIENT_ID}
            value={filters['po_pin'] || ''}
            onChange={e => handleFilterChange('po_pin', e.target.value)}
          />
        </div>
        <div style={{ width: 130 }}>
          <FyzksInput
            size="small"
            placeholder={FieldLabels.ACCESSION_NUMBER_SHORTEST}
            value={filters['po_acc_no'] || ''}
            onChange={e => handleFilterChange('po_acc_no', e.target.value)}
          />
        </div>
        <div style={{ width: 150 }}>
          <FyzksInput
            size="small"
            placeholder={FieldLabels.REFERRING_DOCTOR}
            value={filters['po_ref_doc'] || ''}
            onChange={e => handleFilterChange('po_ref_doc', e.target.value)}
          />
        </div>
        <div style={{ width: 170 }}>
          <FyzksInput
            size="small"
            placeholder="Body Part / Study Desc"
            value={filters['po_body_part'] || ''}
            onChange={e => handleFilterChange('po_body_part', e.target.value)}
          />
        </div>
        <div style={{ width: 150 }}>
          <Select
            size="small"
            mode="multiple"
            allowClear
            placeholder="Status"
            value={filters['pacs_status']}
            style={{ width: '100%' }}
            options={statusOptions}
            onChange={val => handleFilterChange('pacs_status', val)}
          />
        </div>
        <div style={{ width: 150 }}>
          <Select
            size="small"
            mode="multiple"
            allowClear
            placeholder="Center"
            value={filters['center']}
            style={{ width: '100%' }}
            options={centers}
            onChange={val => handleFilterChange('center', val)}
          />
        </div>
        <div style={{ width: 150 }}>
          <Select
            size="small"
            mode="multiple"
            allowClear
            placeholder="HIS Status"
            value={filters['po_his_status']}
            style={{ width: '100%' }}
            options={hisStatusOptions}
            onChange={val => handleFilterChange('po_his_status', val)}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <RangePicker
            size="small"
            placeholder={[FieldLabels.STUDY_DATE, '']}
            showTime={{
              format: 'HH:mm',
              use12Hours: false,
              defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
            }}
            format="YYYY-MM-DD HH:mm"
            value={dateRange}
            onChange={val => setDateRange(val ? [val[0], val[1]] : null)}
          />
          <QuickDatePresets value={dateRange} onChange={setDateRange} />
        </div>
        <Button
          size="small"
          type="primary"
          onClick={() => {
            setPagination({ current: 1, pageSize: pagination.pageSize });
            fetchStudies(1, pagination.pageSize);
          }}
        >
          Search
        </Button>
      </div>

      {/* Modality checkboxes — same behavior as the PACS list */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 500 }}>Modality</span>
        <Checkbox
          checked={modalityAllSelected}
          indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
          onChange={e => {
            setModalityAllSelected(e.target.checked);
            handleFilterChange('modality', []);
          }}
        >
          Select all
        </Checkbox>
        <Checkbox.Group
          value={modalityAllSelected ? modalityOptions.map(o => o.value) : (filters['modality'] || [])}
          onChange={vals => {
            const allChecked = vals.length === modalityOptions.length;
            setModalityAllSelected(allChecked);
            handleFilterChange('modality', allChecked ? [] : vals);
          }}
        >
          {modalityOptions.map(opt => (
            <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
          ))}
        </Checkbox.Group>
      </div>

      <Table
        size="small"
        rowKey={rec => rec?.pacs_order?.pacs_ord_id}
        columns={columns}
        dataSource={studies.data}
        loading={studies.loading}
        scroll={{ x: 1100 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: studies.total,
          showSizeChanger: true,
          showTotal: total => `${total} studies`,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
            fetchStudies(page, pageSize);
          },
        }}
      />

      {editModal.record && (
        <EditStudyDetailsModal
          visible={editModal.visible}
          onCancel={() => setEditModal({ visible: false, record: null })}
          onSuccess={() => fetchStudies(pagination.current, pagination.pageSize)}
          studyData={{
            patientName: editModal.record?.pacs_order?.patient?.pat_name || '',
            patientId: editModal.record?.pacs_order?.patient?.pat_pin || '',
            patientAge: editModal.record?.pacs_order?.patient?.pat_age || '',
            patientDOB: editModal.record?.pacs_order?.patient?.pat_dob,
            patientGender: editModal.record?.pacs_order?.patient?.pat_sex || '',
            studyDescription: editModal.record?.pacs_order?.po_diag_desc || '',
            accessionNumber: editModal.record?.pacs_order?.po_acc_no || '',
            referringPhysician: editModal.record?.pacs_order?.po_ref_doc || '',
            history: editModal.record?.pacs_order?.po_history || '',
            studyUID: editModal.record?.ps_study_uid,
            orderId: editModal.record?.pacs_order?.pacs_ord_id,
          }}
        />
      )}

      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            Delete Study Permanently
          </span>
        }
        open={deleteModal.visible}
        okText="Delete Permanently"
        okButtonProps={{ danger: true, loading: deleting }}
        onOk={submitDelete}
        onCancel={() => setDeleteModal({ visible: false, record: null })}
      >
        <div style={{ marginBottom: 8 }}>
          Patient: <b>{deleteRec?.pacs_order?.patient?.pat_name || '-'}</b>
          {deleteRec?.pacs_order?.po_acc_no ? ` • Acc: ${deleteRec.pacs_order.po_acc_no}` : ''}
        </div>
        <p style={{ marginBottom: 8 }}>
          This PERMANENTLY deletes the study from the DICOM server (images cannot be restored)
          and clears all reports and study data. The order reverts to PENDING, so the same
          study can be re-uploaded and will arrive as a fresh scan.
        </p>
        <Input.TextArea
          rows={3}
          value={deleteReason}
          placeholder="Reason for deletion (optional)"
          onChange={e => setDeleteReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ManageStudies;
